<template>
  <div v-if="matchedContracts">
    <div class="flex items-center justify-between mb-4">
      <Pills v-model="selectedCategory" :options="categories" />
      <SecondaryButton :loading="isExporting" @click="exportCSV">
        <DownloadIcon v-if="!isExporting" class="h-4 w-4 mr-2" />
        {{ t("export_button") }}
      </SecondaryButton>
    </div>

    <div class="space-y-6">
      <div v-for="group in visibleGroups" :key="group.category">
        <Table :columns="columns" :data="group.contracts">
          <template #price="{ row }">
            <FieldSpan :value="formatPrice(row.price)" style-to-add="bg-indigo-100 font-semibold" />
          </template>

          <template #termLength="{ row }">
            <FieldSpan :value="`${row.contract.termLength} mois`" style-to-add="bg-gray-100" />
          </template>

          <template #frequency="{ row }">
            <FieldSpan :value="row.monthly ? 'Mensuel' : 'Annuel'" />
          </template>

          <template #priceBand="{ row }">
            {{ formatPrice(row.contract.priceBandMin) }} – {{ formatPrice(row.contract.priceBandMax) }}
          </template>

          <template #insurancePremium="{ row }">
            {{ formatPrice(row.contract.insurancePremium) }}
          </template>

          <template #distributionFee="{ row }">
            {{ formatPercentage(row.distributionFee) }}
          </template>

          <template #merchant="{ row }">
            <span>
              {{ formatPrice(row.storeCommissionAmount) }}
              <span v-if="row.storeAdditionnalFeeAmount" class="text-xs text-gray-500">
                (dont {{ formatPrice(row.storeAdditionnalFeeAmount) }} frais add.)
              </span>
            </span>
          </template>

          <template #estalyFee="{ row }">
            <span>
              {{ formatPercentage(row.estalyFee) }}
              <span v-if="row.estalyAdditionnalFeeAmount" class="text-xs text-gray-500">
                (+ {{ formatPrice(row.estalyAdditionnalFeeAmount) }} frais add.)
              </span>
            </span>
          </template>
        </Table>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, FieldSpan, Pills, SecondaryButton, formatPrice, formatPercentage, download } from "@estaly/ui";
import { DownloadIcon } from "@heroicons/vue/outline";
import { mapActions } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: { Table, FieldSpan, Pills, SecondaryButton, DownloadIcon },
  props: {
    matchedContracts: { type: Array, required: true },
    storeId: { type: Number, required: true },
    salesChannel: { type: String, default: "" },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      selectedCategory: null,
      isExporting: false,
      columns: [
        { field: "id", label: "ID", bold: true },
        { field: "title", label: "Offre" },
        { field: "referenceId", label: "Référence" },
        { field: "price", label: "Prix", slot: "price" },
        { field: "termLength", label: "Durée", slot: "termLength" },
        { field: "frequency", label: "Fréquence", slot: "frequency" },
        { field: "priceBand", label: "Tranche", slot: "priceBand" },
        { field: "insurancePremium", label: "Prime", slot: "insurancePremium" },
        { field: "distributionFee", label: "Com. distribution", slot: "distributionFee" },
        { field: "storeCommissionAmount", label: "Marchand", slot: "merchant" },
        { field: "estalyFee", label: "Estaly", slot: "estalyFee" },
      ],
    };
  },

  computed: {
    groupedContracts() {
      const groups = new Map();
      for (const mc of this.matchedContracts) {
        const key = mc.businessVertical?.category ?? "Sans catégorie";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(mc);
      }
      return [...groups.entries()]
        .map(([category, contracts]) => ({ category, contracts }))
        .sort((a, b) => a.category.localeCompare(b.category, "fr"));
    },
    categories() {
      return this.groupedContracts.map((g) => g.category);
    },
    visibleGroups() {
      if (this.selectedCategory == null) return this.groupedContracts;
      return this.groupedContracts.filter((g) => g.category === this.selectedCategory);
    },
  },

  watch: {
    categories: {
      immediate: true,
      handler(newCategories) {
        if (newCategories.length === 0) {
          this.selectedCategory = null;
        } else if (!newCategories.includes(this.selectedCategory)) {
          this.selectedCategory = newCategories[0];
        }
      },
    },
  },

  methods: {
    formatPrice,
    formatPercentage,
    ...mapActions("notifications", ["notify"]),
    ...mapActions("matchedContracts", { exportMatchedContractsCSV: "exportCSV" }),

    async exportCSV() {
      this.isExporting = true;
      try {
        const response = await this.exportMatchedContractsCSV({ storeId: this.storeId });

        if (response?.status === 204) {
          return this.notify({
            category: "simple",
            type: "error",
            title: this.t("no_offers_to_export"),
          });
        }

        if (!response?.success) return;

        download(response.data, `matched_contracts_${this.salesChannel}.csv`);
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<i18n src="./StoreMatchedContracts.i18n.json"></i18n>
